import { useEffect } from "react";

type UseMouseTouchBridgeParams = {
  isCloudMode: boolean;
  interactionMode: string;
};

export function useMouseTouchBridge({ isCloudMode, interactionMode }: UseMouseTouchBridgeParams) {
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const enableMouseAsTouch = searchParams.get("mouseAsTouch") === "1" || isCloudMode || interactionMode === "hybrid";
    if (!enableMouseAsTouch) {
      return;
    }

    let touching = false;

    const shouldSkipMouseTouchBridge = (target: Element | null) => {
      if (!target) {
        return true;
      }

      // 文本输入和虚拟键盘保留原生鼠标行为，避免再次出现一次点击被输入两次的问题。
      // 但 range 滑杆要保留桥接，否则鼠标模式下滑动结束后不会真正提交到外层状态。
      return Boolean(target.closest([
        "input:not([type='range'])",
        "textarea",
        "select",
        "[contenteditable='true']",
        ".simple-keyboard",
        ".hg-button",
        ".simple-keyboard-preview",
      ].join(",")));
    };

    // 鼠标模式下继续补一层触摸桥接，让滑杆和滚轮组件还能走原本的触摸链路。
    const dispatchTouchLikeEvent = (type: "touchstart" | "touchmove" | "touchend", evt: MouseEvent) => {
      const target = evt.target as Element | null;
      if (!target || shouldSkipMouseTouchBridge(target)) {
        return;
      }

      try {
        const pointerType = type === "touchstart" ? "pointerdown" : type === "touchmove" ? "pointermove" : "pointerup";
        target.dispatchEvent(new PointerEvent(pointerType, {
          bubbles: true,
          cancelable: true,
          clientX: evt.clientX,
          clientY: evt.clientY,
          pointerType: "touch",
          isPrimary: true,
        }));
      } catch (error) {
        // 旧浏览器不支持 PointerEvent 时继续走 TouchEvent 或普通事件降级。
      }

      try {
        const TouchCtor = (window as any).Touch;
        const TouchEventCtor = (window as any).TouchEvent;
        if (TouchCtor && TouchEventCtor) {
          const touch = new TouchCtor({
            identifier: 1,
            target,
            clientX: evt.clientX,
            clientY: evt.clientY,
            screenX: evt.screenX,
            screenY: evt.screenY,
            pageX: evt.pageX,
            pageY: evt.pageY,
            radiusX: 1,
            radiusY: 1,
            rotationAngle: 0,
            force: 1,
          });
          const touches = type === "touchend" ? [] : [touch];
          const touchEvent = new TouchEventCtor(type, {
            bubbles: true,
            cancelable: true,
            touches,
            targetTouches: touches,
            changedTouches: [touch],
          });
          target.dispatchEvent(touchEvent);
          return;
        }
      } catch (error) {
        // Touch 构造器不可用时继续走最轻量的 Event 兜底。
      }

      target.dispatchEvent(new Event(type, { bubbles: true, cancelable: true }));
    };

    const onMouseDown = (evt: MouseEvent) => {
      if (evt.button !== 0) {
        return;
      }
      touching = true;
      dispatchTouchLikeEvent("touchstart", evt);
    };

    const onMouseMove = (evt: MouseEvent) => {
      if (!touching) {
        return;
      }
      dispatchTouchLikeEvent("touchmove", evt);
    };

    const onMouseUp = (evt: MouseEvent) => {
      if (!touching) {
        return;
      }
      touching = false;
      dispatchTouchLikeEvent("touchend", evt);
    };

    window.addEventListener("mousedown", onMouseDown, true);
    window.addEventListener("mousemove", onMouseMove, true);
    window.addEventListener("mouseup", onMouseUp, true);

    return () => {
      window.removeEventListener("mousedown", onMouseDown, true);
      window.removeEventListener("mousemove", onMouseMove, true);
      window.removeEventListener("mouseup", onMouseUp, true);
    };
  }, [interactionMode, isCloudMode]);
}
